import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  CatalogPropertiesService,
  type CatalogProperty,
} from 'src/api/generated';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { PropertyPermission } from 'src/modules/shared/types/permissions.type';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';

/** Property response data and the identifiers still awaiting authorization or a response. */
interface ReferencedPropertiesResult {
  /** Successfully loaded, authorized property responses. */
  properties: ReadonlyMap<number, CatalogProperty>;
  /** Pending IDs; settled missing, denied and failed reads are not included. */
  loadingPropertyIds: ReadonlySet<number>;
}

const staleTime = 60_000;
const batchSize = 50;

/**
 * Load authorized properties in bounded catalog-list batches, sharing the detail cache.
 * Consumers retain their own labels while properties are loading or unavailable.
 * @param propertyIds - Property IDs visible in the consuming view.
 * @returns Successful authorized properties, with display codes resolved by the API.
 */
export function useReferencedProperties(
  propertyIds: readonly (number | null | undefined)[]
): ReferencedPropertiesResult {
  const queryClient = useQueryClient();
  const { hasPermission: canRead, loading } = useHasPermissions(
    PropertyPermission.Read
  );
  const { hasPermission: canReadScoped, loading: scopedLoading } =
    useHasPermissions(PropertyPermission.ReadScoped);
  const enabled = !loading && !scopedLoading && (canRead || canReadScoped);
  const requestedIds = [
    ...new Set(
      propertyIds.filter(
        (id): id is number =>
          typeof id === 'number' &&
          Number.isInteger(id) &&
          id > 0 &&
          id <= 2147483647
      )
    ),
  ].sort((a, b) => a - b);
  const ids = enabled ? requestedIds : [];
  const batches: number[][] = [];
  for (let offset = 0; offset < ids.length; offset += batchSize) {
    batches.push(ids.slice(offset, offset + batchSize));
  }
  const freshDetail = (id: number) => {
    const state = queryClient.getQueryState<CatalogProperty>([
      'catalog/properties',
      'detail',
      id,
    ]);
    return state?.status === 'success' &&
      !state.isInvalidated &&
      Date.now() - state.dataUpdatedAt < staleTime
      ? state
      : undefined;
  };
  const queries = useQueries({
    queries: batches.map((batch) => ({
      queryKey: ['catalog/properties', 'references', batch],
      queryFn: async () => {
        const cached = batch.flatMap((id) => freshDetail(id)?.data ?? []);
        const cachedIds = new Set(cached.map((property) => property.id));
        const missing = batch.filter((id) => !cachedIds.has(id));
        if (missing.length === 0) return cached;
        const { filterBy, pageSize } = createQuery()
          .equals('id', missing)
          .take(missing.length)
          .build();
        const response = await CatalogPropertiesService.listProperties(
          filterBy,
          undefined,
          undefined,
          1,
          pageSize
        );
        for (const property of response.items) {
          queryClient.setQueryData(
            ['catalog/properties', 'detail', property.id],
            property
          );
        }
        return [...cached, ...response.items];
      },
      initialData: () => {
        const cached = batch.flatMap((id) => freshDetail(id)?.data ?? []);
        return cached.length === batch.length ? cached : undefined;
      },
      initialDataUpdatedAt: () =>
        Math.min(...batch.map((id) => freshDetail(id)?.dataUpdatedAt ?? 0)),
      staleTime,
      retry: false,
      retryOnMount: false,
    })),
  });
  const properties = new Map(
    queries.flatMap((query) =>
      query.isSuccess
        ? query.data.map((property) => [property.id, property] as const)
        : []
    )
  );
  const loadingPropertyIds = new Set(
    loading || scopedLoading
      ? requestedIds
      : queries.flatMap((query, index) =>
          query.isPending ? batches[index] : []
        )
  );
  return { properties, loadingPropertyIds };
}
